import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { SectionHeader, EmptyState } from "@/components/Shared";
import RolesEditor from "@/components/RolesEditor";
import VorlagenHub from "@/components/VorlagenHub";
import FuhrparkMasterHub from "@/components/FuhrparkMasterHub";
import AutoGenRulesAdmin from "@/components/AutoGenRulesAdmin";
import { toast } from "sonner";
import {
  X, Building2, ExternalLink, ChevronDown, ChevronRight, Shield, Clock, Database,
  Plug, ServerCog, Lock,
} from "lucide-react";
import { Link } from "react-router-dom";
import ModuleHint from "@/components/ModuleHint";
import HolidaysAdmin from "@/components/HolidaysAdmin";
import BackupDataHub from "@/components/BackupDataHub";
import EmailSmsConfig from "@/components/EmailSmsConfig";
import FirmsAdmin from "@/components/FirmsAdmin";
import SchemaDriftStatusCard from "@/components/SchemaDriftStatusCard";
import { fmtCur, fmtDateTime } from "@/lib/format";
import { usePermissions } from "@/contexts/PermissionsContext";
import { useBulkSelect, BulkSelectAll, BulkSelectCheckbox, BulkDeleteBar } from "@/components/BulkDeleteBar";

// Phase G+ Phase 5b·v2 (19.06.2026) — Final layout per user direction:
// • RESTORED: Auto-Generator (kritisch für Massenlauf + Auto-Kontrolle),
//   Fuhrpark, Vorlagen (Dienstplan-Schichten etc.) — alles wieder drin
//   weil sie die Lohnabrechnung indirekt steuern.
// • REMOVED: APIs, Lenkerauskunft, Steuerberater (User: "API options
//   all deleted") — nur E-Mail-Benachrichtigung für ablaufende
//   Dokumente / Pickerl bleibt unter „Benachrichtigungen".
// • REMOVED dauerhaft (jetzt in Lohn & Zeit oder Firmenprofil):
//   Lohnabrechnung, Arbeitszeit, Checklisten, Rechnung & Steuer.
const TAB_GROUPS = [
  {
    key: "organisation",
    label: "Organisation",
    icon: Building2,
    tabs: ["Abteilungen", "Firmen", "Einsatzorte", "Feiertage"],
  },
  {
    key: "users_security",
    label: "Benutzer & Sicherheit",
    icon: Shield,
    tabs: ["Benutzer", "Rollen"],
  },
  {
    key: "payroll_time",
    label: "Lohn & Zeit",
    icon: Clock,
    tabs: ["Auto-Generator"],
  },
  {
    key: "master_data",
    label: "Stammdaten",
    icon: Database,
    tabs: ["Fuhrpark", "Vorlagen"],
  },
  {
    key: "notifications",
    label: "Benachrichtigungen",
    icon: Plug,
    tabs: ["E-Mail-Alerts"],
  },
  {
    key: "system",
    label: "System",
    icon: ServerCog,
    adminOnly: true,
    tabs: ["Backup & Datensicherung", "System"],
  },
];

export default function Einstellungen() {
  const { canManageRoles, can, isAdmin } = usePermissions();
  const [tab, setTab] = useState("Abteilungen");
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem("munja.einstellungen.collapsed");
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  // Phase 2 Tier 3 — Add-on gated tabs
  const [addons, setAddons] = useState(null); // null = loading
  useEffect(() => {
    api.get("/extensions").then(({ data }) => {
      const active = new Set((data || []).filter(e => e.is_active).map(e => e.key));
      setAddons(active);
    }).catch(() => setAddons(new Set()));
  }, []);
  const hasPayroll = !addons || addons.has("payroll_processing");
  const hasTime = !addons || addons.has("time_tracking");
  const hasDriving = !addons || addons.has("driving_logs");

  // Each settings tab is gated by its own fine-grained permission module.
  // Phase G+ Phase 5b·v2 — see TAB_GROUPS above for rationale.
  const TAB_DEFS = [
    { key: "Abteilungen",       module: "einstellungen_abteilungen",       gate: true },
    { key: "Firmen",            module: "einstellungen_firmen",            gate: canManageRoles },
    { key: "Einsatzorte",       module: "einstellungen_einsatzorte",       gate: true },
    { key: "Benutzer",          module: "einstellungen_benutzer",          gate: true },
    { key: "Rollen",            module: "einstellungen_rollen",            gate: canManageRoles },
    { key: "Feiertage",         module: "einstellungen_feiertage",         gate: true },
    { key: "Auto-Generator",    module: "einstellungen_auto_generator",    gate: hasTime },
    { key: "Fuhrpark",          module: "einstellungen_fuhrpark",          gate: true },
    { key: "Vorlagen",          module: "einstellungen_vorlagen",          gate: true },
    { key: "E-Mail-Alerts",     module: "einstellungen_email_sms",         gate: true },
    { key: "Backup & Datensicherung", module: "einstellungen_backup",      gate: isAdmin },
    { key: "System",            module: "einstellungen_system",            gate: isAdmin },
  ];
  const TAB_VISIBLE = new Set(TAB_DEFS.filter(t => t.gate && can(t.module, "view")).map(t => t.key));
  // Build groups with only-visible tabs
  const visibleGroups = TAB_GROUPS
    .map(g => ({ ...g, tabs: g.tabs.filter(k => TAB_VISIBLE.has(k)) }))
    .filter(g => g.tabs.length > 0);
  const TABS = visibleGroups.flatMap(g => g.tabs);

  // If the current tab is no longer available, redirect to first available.
  useEffect(() => { if (!TABS.includes(tab)) setTab(TABS[0] || "Abteilungen"); // eslint-disable-next-line
    }, [hasPayroll, hasTime, hasDriving, canManageRoles, TABS.join("|")]);

  function toggleGroup(key) {
    const next = { ...collapsed, [key]: !collapsed[key] };
    setCollapsed(next);
    try { localStorage.setItem("munja.einstellungen.collapsed", JSON.stringify(next)); } catch {}
  }

  return (
    <div className="p-6 md:p-8" data-testid="einstellungen-page">
      <SectionHeader title="Einstellungen" subtitle="Abteilungen, Einsatzorte, Benutzer, Rollen, Templates" />
      <ModuleHint moduleKey="einstellungen" />

      {/* Hint: company info moved to /firmenprofil */}
      <Link to="/firmenprofil" data-testid="firmenprofil-redirect-hint"
        className="mb-5 flex items-center justify-between gap-3 px-4 py-3 bg-blue-500/5 hover:bg-blue-500/10 border border-blue-500/20 rounded-md transition-colors group">
        <div className="flex items-center gap-3">
          <Building2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
          <div>
            <div className="text-sm text-zinc-100 font-medium">Firmen­profil &amp; Branding</div>
            <div className="text-xs text-zinc-500">Logo, Farben, Stammdaten, Bankverbindungen, E-Mail-Signaturen — alle Firmen­einstellungen wurden ins eigene Modul „Firmenprofil" verschoben.</div>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 text-xs text-blue-400 group-hover:text-blue-300 flex-shrink-0">
          Öffnen <ExternalLink className="w-3.5 h-3.5" />
        </span>
      </Link>

      {/* Side-Nav layout: left = grouped nav, right = active tab content */}
      <div className="flex flex-col lg:flex-row gap-6" data-testid="einstellungen-layout">
        {/* Left side-nav */}
        <aside className="lg:w-64 lg:shrink-0" data-testid="einstellungen-sidenav">
          <nav className="bg-zinc-900/50 border border-zinc-800 rounded-md p-2 space-y-1 lg:sticky lg:top-4">
            {visibleGroups.map(g => {
              const Icon = g.icon;
              const isOpen = !collapsed[g.key];
              const isAdminGroup = g.adminOnly;
              return (
                <div key={g.key} data-testid={`settings-group-${g.key}`}>
                  <button
                    onClick={() => toggleGroup(g.key)}
                    data-testid={`settings-group-${g.key}-toggle`}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-wider font-semibold rounded transition-colors ${
                      isAdminGroup ? "text-amber-300/80 hover:text-amber-200 hover:bg-amber-500/5"
                                   : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50"
                    }`}
                  >
                    {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <Icon className="w-3.5 h-3.5" />
                    <span className="flex-1 text-left">{g.label}</span>
                    {isAdminGroup && <Lock className="w-3 h-3 text-amber-400/60" />}
                  </button>
                  {isOpen && (
                    <ul className="mt-0.5 mb-2 ml-1 border-l border-zinc-800 pl-2 space-y-0.5">
                      {g.tabs.map(t => {
                        const active = tab === t;
                        return (
                          <li key={t}>
                            <button
                              onClick={() => setTab(t)}
                              data-testid={`settings-tab-${t}`}
                              className={`w-full text-left px-3 py-1.5 text-sm rounded transition-colors ${
                                active
                                  ? "bg-blue-500/15 text-blue-200 font-medium"
                                  : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/50"
                              }`}
                            >
                              {t}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>

        {/* Right content panel */}
        <div className="flex-1 min-w-0" data-testid="einstellungen-content">
          {tab === "Abteilungen" && <DepartmentsAdmin/>}
          {tab === "Firmen" && <FirmsAdmin/>}
          {tab === "Einsatzorte" && <SimpleList endpoint="/settings/workplaces" fields={[["name","Name"],["address","Adresse"],["city","Stadt"]]} label="Einsatzort"/>}
          {tab === "Benutzer" && <UsersList/>}
          {tab === "Rollen" && canManageRoles && <RolesEditor/>}
          {tab === "Feiertage" && <HolidaysAdmin/>}
          {tab === "Auto-Generator" && <AutoGenRulesAdmin/>}
          {tab === "Fuhrpark" && <FuhrparkMasterHub/>}
          {tab === "Vorlagen" && <VorlagenHub/>}
          {tab === "E-Mail-Alerts" && <EmailSmsConfig/>}
          {tab === "Backup & Datensicherung" && <BackupDataHub/>}
          {tab === "System" && <SystemTab/>}
        </div>
      </div>
    </div>
  );
}

function SystemTab() {
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  async function runCompliance() {
    setRunning(true);
    try {
      const { data } = await api.post("/jobs/auto-compliance-tasks");
      setLastRun(data);
      toast.success(`Auto-Compliance fertig — ${data.created} neu, ${data.skipped} bereits offen`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Job fehlgeschlagen");
    } finally { setRunning(false); }
  }

  async function cleanupCompliance() {
    if (!window.confirm("Alle offenen Auto-Compliance-Aufgaben löschen?\n\n(Bereits übernommene oder erledigte bleiben.)")) return;
    setRunning(true);
    try {
      const { data } = await api.delete("/jobs/auto-compliance-tasks");
      toast.success(`${data.deleted} offene Auto-Aufgaben gelöscht`);
      setLastRun(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally { setRunning(false); }
  }

  return (
    <div className="space-y-4">
      <SchemaDriftStatusCard />

      <div className="bg-zinc-900 border border-zinc-800 rounded p-6 space-y-2 text-sm text-zinc-300" data-testid="system-info">
        <div className="flex justify-between"><span>Zeitzone</span><span className="font-mono text-zinc-400">Europe/Vienna</span></div>
        <div className="flex justify-between"><span>Sprache</span><span className="font-mono text-zinc-400">Deutsch (AT)</span></div>
        <div className="flex justify-between"><span>Währung</span><span className="font-mono text-zinc-400">EUR</span></div>
        <div className="flex justify-between"><span>Version</span><span className="font-mono text-zinc-400">1.1.0 (Production-Ready)</span></div>
      </div>

      <div className="rounded-md border border-blue-500/20 bg-blue-500/5 px-4 py-3 text-sm text-blue-200" data-testid="backup-relocated-hint">
        <b>Backups & Wiederherstellung</b> wurden in den eigenen Admin-Only Tab
        „Backup &amp; Datensicherung" verschoben (links in der Tab-Leiste).
        Dort findest du JSON/ZIP-Datenexporte, System-Backup/Restore,
        Disaster-Recovery und Backup-Health an einem Ort.
      </div>

      {/* Phase 32 — Manual auto-compliance trigger */}
      <div className="bg-zinc-900 border border-zinc-800 rounded p-6" data-testid="auto-compliance-section">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <h3 className="font-display text-zinc-50 font-semibold">Auto-Compliance-Aufgaben</h3>
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700">
            Tägliche Ausführung: <span className="text-amber-300 font-bold">DEAKTIVIERT</span>
          </span>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          Scannt aktive Fahrzeuge (Pickerl §57a, Vignette/Maut, Versicherung, Service) und Mitarbeiter (Führerschein-Ablauf) und legt Office-Aufgaben für Termine in <b>30 / 14 / 3 / 0 Tagen</b> an.
          {" "}<b className="text-amber-300">Die automatische tägliche Ausführung ist aktuell aus</b> — bis alle Stamm­daten vollständig erfasst sind, würden sonst zu viele Aufgaben aus alten/leeren Datensätzen entstehen.
          {" "}Wenn du bereit bist, setze <code className="text-amber-300 font-mono text-[10px] bg-zinc-950 px-1.5 py-0.5 rounded">AUTO_COMPLIANCE_ENABLED=true</code> in der Backend-Umgebung. Bis dahin kannst du den Scan unten jederzeit manuell anstoßen.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={runCompliance}
            disabled={running}
            data-testid="auto-compliance-run"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-800 disabled:text-zinc-500 text-zinc-950 rounded font-bold">
            {running ? "Läuft…" : "Jetzt manuell prüfen + Aufgaben anlegen"}
          </button>
          <button
            onClick={cleanupCompliance}
            disabled={running}
            data-testid="auto-compliance-cleanup"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-zinc-200 rounded">
            Offene Auto-Aufgaben löschen
          </button>
        </div>
        {lastRun && (
          <div className="mt-3 text-xs text-zinc-300 bg-zinc-950 border border-zinc-800 rounded px-3 py-2 font-mono">
            Tenants: {lastRun.tenants} · neu: <span className="text-emerald-400">{lastRun.created}</span> · bereits offen: <span className="text-zinc-400">{lastRun.skipped}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function DepartmentsAdmin() {
  const [items, setItems] = useState([]);
  const [firms, setFirms] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", firm_id: "" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", description: "", firm_id: "" });
  const sel = useBulkSelect(items);

  async function load() {
    try {
      const [d, f] = await Promise.all([
        api.get("/settings/departments"),
        api.get("/firms"),
      ]);
      setItems(d.data || []);
      setFirms(f.data || []);
      const defaultFirm = (f.data || []).find(x => x.is_default) || (f.data || [])[0];
      if (defaultFirm && !form.firm_id) setForm(s => ({ ...s, firm_id: defaultFirm.id }));
    } catch { /* ignore */ }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function add() {
    if (!form.name.trim()) { toast.error("Name erforderlich"); return; }
    try {
      await api.post("/settings/departments", form);
      toast.success("Abteilung hinzugefügt");
      setForm({ name: "", description: "", firm_id: form.firm_id });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }
  function startEdit(d) {
    setEditingId(d.id);
    setEditForm({ name: d.name || "", description: d.description || "", firm_id: d.firm_id || "" });
  }
  async function saveEdit() {
    try {
      await api.put(`/settings/departments/${editingId}`, editForm);
      toast.success("Aktualisiert");
      setEditingId(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }
  async function remove(id) {
    if (!window.confirm("Abteilung löschen?")) return;
    try { await api.delete(`/settings/departments/${id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }
  const firmName = (id) => firms.find(f => f.id === id)?.name || "—";
  const firmColor = (id) => firms.find(f => f.id === id)?.color || "#64748b";

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded overflow-hidden" data-testid="departments-admin">
      <div className="p-4 border-b border-zinc-800 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2">
        <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="dept-new-name"
          className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100" />
        <input placeholder="Beschreibung" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          data-testid="dept-new-desc"
          className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100" />
        <select value={form.firm_id} onChange={(e) => setForm({ ...form, firm_id: e.target.value })}
          data-testid="dept-new-firm"
          className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
          <option value="">— Firma wählen —</option>
          {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <button onClick={add} data-testid="dept-add-btn"
          className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded">+ Abteilung</button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-800 bg-zinc-950/50">
            <th className="px-3 py-2 w-8"><BulkSelectAll sel={sel} testid="dept-select-all" /></th>
            <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Name</th>
            <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Beschreibung</th>
            <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Firma</th>
            <th className="px-4 py-2 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-semibold w-32">Aktion</th>
          </tr>
        </thead>
        <tbody>
          {items.map(d => (
            <tr key={d.id} className="border-b border-zinc-800/50 hover:bg-zinc-950/40" data-testid={`dept-row-${d.id}`}>
              {editingId === d.id ? (
                <>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2">
                    <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full px-2 py-1 bg-zinc-950 border border-zinc-800 rounded text-sm" />
                  </td>
                  <td className="px-3 py-2">
                    <input value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      className="w-full px-2 py-1 bg-zinc-950 border border-zinc-800 rounded text-sm" />
                  </td>
                  <td className="px-3 py-2">
                    <select value={editForm.firm_id} onChange={(e) => setEditForm({ ...editForm, firm_id: e.target.value })}
                      data-testid={`dept-edit-firm-${d.id}`}
                      className="w-full px-2 py-1 bg-zinc-950 border border-zinc-800 rounded text-sm">
                      <option value="">— Firma —</option>
                      {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={saveEdit} className="text-xs text-emerald-400 mr-2" data-testid={`dept-save-${d.id}`}>Speichern</button>
                    <button onClick={() => setEditingId(null)} className="text-xs text-zinc-400">Abbruch</button>
                  </td>
                </>
              ) : (
                <>
                  <td className="px-3 py-2.5 text-center">
                    <BulkSelectCheckbox sel={sel} id={d.id} testid={`dept-select-${d.id}`} />
                  </td>
                  <td className="px-4 py-2.5 text-zinc-100 font-medium">{d.name || "—"}</td>
                  <td className="px-4 py-2.5 text-zinc-400">{d.description || "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: firmColor(d.firm_id) }} />
                      <span className="text-zinc-300 text-xs">{firmName(d.firm_id)}</span>
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => startEdit(d)} className="text-xs text-blue-400 hover:text-blue-300 mr-3" data-testid={`dept-edit-${d.id}`}>Bearbeiten</button>
                    <button onClick={() => remove(d.id)} className="text-xs text-red-400 hover:text-red-300" data-testid={`dept-delete-${d.id}`}>Löschen</button>
                  </td>
                </>
              )}
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-zinc-500">Keine Abteilungen angelegt.</td></tr>
          )}
        </tbody>
      </table>
      <BulkDeleteBar
        sel={sel}
        label="Abteilungen"
        deleteEndpoint={(id) => `/settings/departments/${id}`}
        onAfter={load}
        testid="dept-bulk-delete"
      />
    </div>
  );
}

function SimpleList({ endpoint, fields, label }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(Object.fromEntries(fields.map(([k])=>[k,""])));
  const sel = useBulkSelect(items);
  async function load() { const { data } = await api.get(endpoint); setItems(data); }
  useEffect(() => { load(); }, []);
  async function add() {
    try { await api.post(endpoint, form); toast.success("Hinzugefügt"); setForm(Object.fromEntries(fields.map(([k])=>[k,""]))); load(); } catch { toast.error("Fehler"); }
  }
  async function remove(id) { await api.delete(`${endpoint}/${id}`); load(); }
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded overflow-hidden">
      <div className="p-4 border-b border-zinc-800 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2">
        {fields.map(([k, l]) => (
          <input key={k} placeholder={l} value={form[k]} onChange={(e)=>setForm({...form,[k]:e.target.value})} className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
        ))}
        <button onClick={add} className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded">+ {label}</button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-800 bg-zinc-950/50">
            <th className="px-3 py-2 w-8"><BulkSelectAll sel={sel} testid={`${label.toLowerCase()}-select-all`} /></th>
            {fields.map(([k, l]) => (
              <th key={k} className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{l}</th>
            ))}
            <th className="px-4 py-2 text-right text-[10px] uppercase tracking-wider text-zinc-500 font-semibold w-24">Aktion</th>
          </tr>
        </thead>
        <tbody>
          {items.map(i => (
            <tr key={i.id} className="border-b border-zinc-800">
              <td className="px-3 py-2.5 text-center">
                <BulkSelectCheckbox sel={sel} id={i.id} testid={`${label.toLowerCase()}-select-${i.id}`} />
              </td>
              {fields.map(([k]) => <td key={k} className="px-4 py-2.5 text-zinc-300">{i[k]||"—"}</td>)}
              <td className="px-4 py-2.5 text-right"><button onClick={()=>remove(i.id)} className="text-xs text-red-400 hover:text-red-300">Löschen</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <BulkDeleteBar
        sel={sel}
        label={label}
        deleteEndpoint={(id) => `${endpoint}/${id}`}
        onAfter={load}
        testid={`${label.toLowerCase()}-bulk-delete`}
      />
    </div>
  );
}

function UsersList() {
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [firms, setFirms] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "viewer" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ role: "", department_ids: [], firm_ids: [] });
  // Phase A — Passwort-Reset Modal
  const [resetUid, setResetUid] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [resetBusy, setResetBusy] = useState(false);

  async function resetPassword() {
    if (resetPw.length < 8) { toast.error("Passwort muss min. 8 Zeichen lang sein"); return; }
    setResetBusy(true);
    try {
      await api.post(`/settings/users/${resetUid}/reset-password`, { new_password: resetPw });
      toast.success("Passwort wurde zurückgesetzt. Der Benutzer muss es beim nächsten Login ändern.");
      setResetUid(null); setResetPw("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setResetBusy(false); }
  }

  async function load() {
    try {
      const [u, d, f] = await Promise.all([
        api.get("/settings/users"),
        api.get("/departments"),
        api.get("/firms").catch(() => ({ data: [] })),
      ]);
      setUsers(u.data); setDepts(d.data); setFirms(f.data);
    } catch { /* ignore */ }
  }
  useEffect(() => { load(); }, []);

  async function add() {
    try { await api.post("/settings/users", form); toast.success("Benutzer angelegt"); setForm({ email: "", name: "", password: "", role: "viewer" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }

  function startEdit(u) {
    setEditingId(u.id);
    setEditForm({
      role: u.role || "viewer",
      department_ids: u.department_ids || [],
      firm_ids: u.firm_ids || [],
    });
  }
  async function saveEdit(uid) {
    try {
      await api.put(`/settings/users/${uid}`, { role: editForm.role, department_ids: editForm.department_ids });
      // Also persist firm_ids via the dedicated endpoint
      await api.put(`/users/${uid}/firms`, { firm_ids: editForm.firm_ids || [] }).catch(() => null);
      toast.success("Aktualisiert"); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }
  async function remove(uid) {
    if (!window.confirm("Benutzer löschen?")) return;
    try { await api.delete(`/settings/users/${uid}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }
  function toggleDept(id) {
    const ids = editForm.department_ids || [];
    setEditForm({ ...editForm, department_ids: ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id] });
  }
  function toggleFirm(id) {
    const ids = editForm.firm_ids || [];
    setEditForm({ ...editForm, firm_ids: ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id] });
  }

  const deptNames = (ids) => (ids || []).map(id => depts.find(d => d.id === id)?.name).filter(Boolean);
  const isPrivileged = (role) => role === "admin" || role === "geschaeftsfuehrung";

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded overflow-hidden">
      <div className="p-4 border-b border-zinc-800 bg-amber-500/5 text-xs text-amber-300 flex items-start gap-2" data-testid="users-driver-hint">
        <span className="text-base leading-none">ℹ️</span>
        <div>
          <strong className="text-amber-200">Hinweis:</strong> Hier werden nur interne Plattform-Benutzer (Geschäftsführung, Disponent, Fuhrparkleiter, Backoffice, Buchhalter, Viewer) verwaltet. <strong>Fahrer-Zugänge</strong> werden im jeweiligen Mitarbeiter-Profil unter dem Tab <strong>„Portal-Zugang"</strong> erstellt.
        </div>
      </div>
      <div className="p-4 border-b border-zinc-800 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2">
        <input placeholder="Name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
        <input placeholder="E-Mail" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
        <input placeholder="Passwort" type="password" value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
        <select value={form.role} onChange={(e)=>setForm({...form,role:e.target.value})} data-testid="user-role-select" className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
          {["geschaeftsfuehrung","fuhrparkleiter","disponent","abteilungsleiter","backoffice","buchhalter","viewer"].map(r=><option key={r} value={r}>{r}</option>)}
        </select>
        <button onClick={add} data-testid="user-add-btn" className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded">+ Benutzer</button>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
          <th className="px-4 py-3 text-left">Name</th>
          <th className="px-4 py-3 text-left">E-Mail</th>
          <th className="px-4 py-3 text-left">Rolle</th>
          <th className="px-4 py-3 text-left">Firmen-Zugriff</th>
          <th className="px-4 py-3 text-left">Abteilungs-Zugriff</th>
          <th className="px-4 py-3 text-right">Aktionen</th>
        </tr></thead>
        <tbody>{users.map(u => {
          const isEditing = editingId === u.id;
          return (
            <tr key={u.id} className="border-b border-zinc-800 align-top">
              <td className="px-4 py-2.5 text-zinc-100">{u.name}</td>
              <td className="px-4 py-2.5 text-zinc-400 font-mono text-xs">{u.email}</td>
              <td className="px-4 py-2.5 text-zinc-300 capitalize">
                {isEditing ? (
                  <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                    className="px-2 py-1 bg-zinc-950 border border-zinc-800 rounded text-xs text-zinc-100">
                    {(() => {
                      const base = ["geschaeftsfuehrung","fuhrparkleiter","disponent","abteilungsleiter","backoffice","buchhalter","viewer"];
                      const all = base.includes(editForm.role) ? base : [editForm.role, ...base];
                      return all.map(r => <option key={r} value={r}>{r}</option>);
                    })()}
                  </select>
                ) : u.role}
              </td>
              <td className="px-4 py-2.5" data-testid={`user-firms-${u.id}`}>
                {isEditing ? (
                  isPrivileged(editForm.role) ? (
                    <span className="text-[11px] text-zinc-500 italic">Admin/GF sieht alle Firmen</span>
                  ) : (
                    <div className="flex flex-wrap gap-1 max-w-md">
                      {firms.map(f => {
                        const selected = (editForm.firm_ids || []).includes(f.id);
                        return (
                          <button key={f.id} type="button" onClick={() => toggleFirm(f.id)}
                            data-testid={`user-firm-toggle-${f.id}`}
                            className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${selected ? "text-white border-transparent" : "text-zinc-400 border-zinc-700 bg-zinc-950 hover:bg-zinc-800"}`}
                            style={selected ? { backgroundColor: f.color || "#3b82f6" } : {}}>
                            {f.name}
                          </button>
                        );
                      })}
                      {firms.length === 0 && <span className="text-[11px] text-zinc-500 italic">Keine Firmen angelegt</span>}
                    </div>
                  )
                ) : isPrivileged(u.role) ? (
                  <span className="text-[11px] text-zinc-500 italic">(sieht alle)</span>
                ) : (u.firm_ids && u.firm_ids.length > 0) ? (
                  <div className="flex flex-wrap gap-1">
                    {(u.firm_ids || []).map(fid => {
                      const f = firms.find(x => x.id === fid);
                      if (!f) return null;
                      return (
                        <span key={fid} className="text-[10px] px-2 py-0.5 rounded text-white" style={{ backgroundColor: f.color || "#3b82f6" }}>{f.name}</span>
                      );
                    })}
                  </div>
                ) : (
                  <span className="text-[11px] text-zinc-500 italic">Kein Scope — alle Firmen</span>
                )}
              </td>
              <td className="px-4 py-2.5" data-testid={`user-depts-${u.id}`}>
                {isEditing ? (
                  <div className="flex flex-wrap gap-1 max-w-md">
                    {depts.map(d => {
                      const selected = (editForm.department_ids || []).includes(d.id);
                      return (
                        <button key={d.id} type="button" onClick={() => toggleDept(d.id)}
                          data-testid={`user-dept-toggle-${d.id}`}
                          className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${selected ? "text-white border-transparent" : "text-zinc-400 border-zinc-700 bg-zinc-950 hover:bg-zinc-800"}`}
                          style={selected ? { backgroundColor: d.color || "#3b82f6" } : {}}>
                          {d.name}
                        </button>
                      );
                    })}
                    {depts.length === 0 && <span className="text-[11px] text-zinc-500 italic">Keine Abteilungen angelegt</span>}
                  </div>
                ) : u.role === "admin" ? (
                  <span className="text-[11px] text-zinc-500 italic">(Admin sieht alles)</span>
                ) : (u.department_ids && u.department_ids.length > 0) ? (
                  <div className="flex flex-wrap gap-1">
                    {deptNames(u.department_ids).map((n, i) => {
                      const d = depts.find(x => x.name === n);
                      return (
                        <span key={i} className="text-[10px] px-2 py-0.5 rounded text-white" style={{ backgroundColor: d?.color || "#3b82f6" }}>{n}</span>
                      );
                    })}
                  </div>
                ) : (
                  <span className="text-[11px] text-zinc-500 italic">Kein Scope — sieht alles</span>
                )}
              </td>
              <td className="px-4 py-2.5 text-right">
                {isEditing ? (
                  <>
                    <button onClick={() => saveEdit(u.id)} data-testid={`user-save-${u.id}`} className="text-xs text-emerald-400 hover:text-emerald-300 mr-3">Speichern</button>
                    <button onClick={() => setEditingId(null)} className="text-xs text-zinc-400 hover:text-zinc-300">Abbrechen</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => startEdit(u)} data-testid={`user-edit-${u.id}`} className="text-xs text-blue-400 hover:text-blue-300 mr-3">Bearbeiten</button>
                    <button onClick={() => setResetUid(u.id)} data-testid={`user-reset-pw-${u.id}`} className="text-xs text-amber-400 hover:text-amber-300 mr-3">Passwort</button>
                    <button onClick={() => remove(u.id)} className="text-xs text-red-400 hover:text-red-300">Löschen</button>
                  </>
                )}
              </td>
            </tr>
          );
        })}</tbody>
      </table>
      {/* Phase A — Passwort-Reset Modal */}
      {resetUid && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setResetUid(null)}
             data-testid="user-reset-pw-modal">
          <div onClick={e => e.stopPropagation()} className="bg-zinc-900 border border-zinc-700 rounded-lg max-w-md w-full">
            <div className="px-5 py-3 border-b border-zinc-800">
              <h3 className="text-zinc-100 font-semibold">Passwort zurücksetzen</h3>
              <p className="text-[11px] text-zinc-500 mt-1">
                Benutzer: <span className="font-mono text-zinc-300">{users.find(u => u.id === resetUid)?.email}</span>.
                Der Benutzer muss das neue Passwort beim nächsten Login ändern.
              </p>
            </div>
            <div className="p-5 space-y-3">
              <label className="block text-[11px] uppercase tracking-wider text-zinc-500">Neues Passwort</label>
              <input type="password" value={resetPw} onChange={e => setResetPw(e.target.value)}
                placeholder="mind. 8 Zeichen"
                data-testid="user-reset-pw-input"
                className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 font-mono" />
              <p className="text-[10px] text-amber-400">
                💡 Tipp: Generiere ein zufälliges Passwort und teile es dem Benutzer auf einem sicheren Weg mit.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-zinc-800 bg-zinc-950/50 flex justify-end gap-2">
              <button onClick={() => setResetUid(null)} disabled={resetBusy}
                className="px-3 py-1.5 text-xs text-zinc-300 hover:text-white">
                Abbrechen
              </button>
              <button onClick={resetPassword} disabled={resetBusy || resetPw.length < 8}
                data-testid="user-reset-pw-submit"
                className="px-3 py-1.5 text-xs font-semibold bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded">
                {resetBusy ? "Speichere…" : "Passwort setzen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ChecklistsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const EMPTY = { name: "", category: "uebergabe", description: "", items: [{ label: "", required: true }] };
  const [form, setForm] = useState(EMPTY);

  async function load() { const { data } = await api.get("/checklists"); setItems(data); }
  useEffect(() => { load(); }, []);

  async function save() {
    const payload = { ...form, items: form.items.filter(i => i.label.trim()) };
    if (payload.items.length === 0) { toast.error("Mindestens ein Punkt"); return; }
    try {
      if (editing) await api.put(`/checklists/${editing.id}`, payload);
      else await api.post("/checklists", payload);
      toast.success("Gespeichert"); setShowForm(false); setEditing(null); setForm(EMPTY); load();
    } catch { toast.error("Fehler"); }
  }
  async function remove(cid) { if (!window.confirm("Checkliste löschen?")) return; await api.delete(`/checklists/${cid}`); toast.success("Gelöscht"); load(); }
  function startEdit(c) { setEditing(c); setForm({ ...EMPTY, ...c, items: c.items?.length ? c.items : [{label:"",required:true}] }); setShowForm(true); }
  function addItem() { setForm({ ...form, items: [...form.items, { label: "", required: true }] }); }
  function updItem(i, k, v) { const arr = [...form.items]; arr[i] = { ...arr[i], [k]: v }; setForm({ ...form, items: arr }); }
  function delItem(i) { const arr = form.items.filter((_, idx) => idx !== i); setForm({ ...form, items: arr.length ? arr : [{ label:"", required:true }] }); }

  const categories = [
    ["uebergabe","Fahrzeug Übergabe"],["ruecknahme","Fahrzeug Rücknahme"],
    ["anmeldung","Mitarbeiter Anmeldung"],["abmeldung","Mitarbeiter Abmeldung"],
    ["schadensmeldung","Schadensmeldung"],["sonstiges","Sonstiges"],
  ];
  const catLabel = (c) => categories.find(([k]) => k === c)?.[1] || c;

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button data-testid="add-checklist-btn" onClick={()=>{ setEditing(null); setForm(EMPTY); setShowForm(true); }} className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded">
          <span>+</span> Checkliste
        </button>
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded overflow-hidden">
        {items.length === 0 ? <EmptyState label="Keine Checklisten" /> : (
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-3 text-left">Name</th><th className="px-4 py-3 text-left">Kategorie</th>
              <th className="px-4 py-3 text-right">Punkte</th><th className="px-4 py-3 text-right">Aktionen</th>
            </tr></thead>
            <tbody>{items.map(c => (
              <tr key={c.id} className="border-b border-zinc-800 hover:bg-zinc-800/40">
                <td className="px-4 py-2.5 text-zinc-100 font-medium">{c.name}</td>
                <td className="px-4 py-2.5 text-zinc-300">{catLabel(c.category)}</td>
                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-zinc-400">{c.items?.length || 0}</td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={()=>startEdit(c)} className="text-xs text-blue-400 hover:text-blue-300 mr-3">Bearbeiten</button>
                  <button onClick={()=>remove(c.id)} className="text-xs text-red-400 hover:text-red-300">Löschen</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="font-display font-semibold text-zinc-50">{editing ? "Checkliste bearbeiten" : "Neue Checkliste"}</h3>
              <button onClick={()=>setShowForm(false)} className="text-zinc-400">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-[10px] uppercase text-zinc-500">Name *</label>
                  <input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/></div>
                <div><label className="text-[10px] uppercase text-zinc-500">Kategorie</label>
                  <select value={form.category} onChange={(e)=>setForm({...form,category:e.target.value})} className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                    {categories.map(([k,l])=><option key={k} value={k}>{l}</option>)}
                  </select></div>
              </div>
              <div>
                <label className="text-[10px] uppercase text-zinc-500">Beschreibung</label>
                <textarea rows={2} value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] uppercase text-zinc-500">Punkte</label>
                  <button onClick={addItem} className="text-xs text-blue-400 hover:text-blue-300">+ Punkt hinzufügen</button>
                </div>
                <div className="space-y-2">
                  {form.items.map((it, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input value={it.label} onChange={(e)=>updItem(i,"label",e.target.value)} placeholder="z.B. KM-Stand erfasst" className="flex-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
                      <label className="flex items-center gap-1.5 text-xs text-zinc-400 whitespace-nowrap">
                        <input type="checkbox" checked={it.required} onChange={(e)=>updItem(i,"required",e.target.checked)} className="rounded"/> Pflicht
                      </label>
                      <button onClick={()=>delItem(i)} className="text-red-400 hover:text-red-300 px-2">✕</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
              <button onClick={()=>setShowForm(false)} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
              <button onClick={save} data-testid="checklist-save-btn" className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">Speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function TemplatesAdmin() {
  const [items, setItems] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [filter, setFilter] = useState("");
  const EMPTY = { name: "", kind: "email", subject: "", body: "", description: "" };
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(null);

  async function load() {
    const { data } = await api.get("/templates", { params: { kind: filter } }); setItems(data);
  }
  useEffect(() => { load(); api.get("/templates/placeholders").then(({data}) => setPlaceholders(data.placeholders)); }, [filter]);

  async function save() {
    try {
      if (editing) await api.put(`/templates/${editing.id}`, form);
      else await api.post("/templates", form);
      toast.success("Gespeichert"); setShowForm(false); setEditing(null); setForm(EMPTY); load();
    } catch { toast.error("Fehler"); }
  }
  async function remove(tid) {
    if (!window.confirm("Template löschen?")) return;
    await api.delete(`/templates/${tid}`); toast.success("Gelöscht"); load();
  }
  function startEdit(t) { setEditing(t); setForm({...EMPTY, ...t}); setShowForm(true); }
  async function doPreview(t) {
    try {
      const { data } = await api.post(`/templates/${t.id}/preview`, {});
      setPreview({ ...data, name: t.name, kind: t.kind });
    } catch { toast.error("Preview fehlgeschlagen"); }
  }
  function insertPlaceholder(p) {
    setForm(f => ({ ...f, body: (f.body || "") + p }));
  }

  const kindLabel = (k) => ({ email: "E-Mail", sms: "SMS", pdf: "PDF", contract: "Vertrag" })[k] || k;

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <select value={filter} onChange={(e)=>setFilter(e.target.value)} className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
          <option value="">Alle Typen</option>
          {["email","sms","pdf","contract"].map(k => <option key={k} value={k}>{kindLabel(k)}</option>)}
        </select>
        <button data-testid="add-template-btn" onClick={()=>{ setEditing(null); setForm(EMPTY); setShowForm(true); }}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded">+ Template</button>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded overflow-hidden">
        {items.length === 0 ? <EmptyState label="Keine Templates" /> : (
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-3 text-left">Name</th><th className="px-4 py-3 text-left">Typ</th>
              <th className="px-4 py-3 text-left">Betreff / Vorschau</th><th className="px-4 py-3 text-right">Aktionen</th>
            </tr></thead>
            <tbody>{items.map(t => (
              <tr key={t.id} className="border-b border-zinc-800 hover:bg-zinc-800/40">
                <td className="px-4 py-2.5 text-zinc-100 font-medium">{t.name}</td>
                <td className="px-4 py-2.5"><span className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded">{kindLabel(t.kind)}</span></td>
                <td className="px-4 py-2.5 text-zinc-400 text-xs truncate max-w-[400px]">{t.subject || t.body?.slice(0,80)}</td>
                <td className="px-4 py-2.5 text-right space-x-2">
                  <button onClick={()=>doPreview(t)} className="text-xs text-zinc-400 hover:text-zinc-100">Vorschau</button>
                  <button onClick={()=>startEdit(t)} className="text-xs text-blue-400 hover:text-blue-300">Bearbeiten</button>
                  <button onClick={()=>remove(t.id)} className="text-xs text-red-400 hover:text-red-300">Löschen</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="font-display font-semibold text-zinc-50">{editing ? "Template bearbeiten" : "Neues Template"}</h3>
              <button onClick={()=>setShowForm(false)} className="text-zinc-400">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2"><label className="text-[10px] uppercase text-zinc-500">Name *</label>
                  <input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/></div>
                <div><label className="text-[10px] uppercase text-zinc-500">Typ</label>
                  <select value={form.kind} onChange={(e)=>setForm({...form,kind:e.target.value})} className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                    {["email","sms","pdf","contract"].map(k => <option key={k} value={k}>{kindLabel(k)}</option>)}
                  </select></div>
              </div>
              {form.kind !== "sms" && (
                <div><label className="text-[10px] uppercase text-zinc-500">Betreff / Titel</label>
                  <input value={form.subject} onChange={(e)=>setForm({...form,subject:e.target.value})} className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/></div>
              )}
              <div>
                <label className="text-[10px] uppercase text-zinc-500">Inhalt *</label>
                <textarea rows={10} value={form.body} onChange={(e)=>setForm({...form,body:e.target.value})} className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 font-mono"/>
              </div>
              <div>
                <div className="text-[10px] uppercase text-zinc-500 mb-2">Platzhalter (klicken zum Einfügen)</div>
                <div className="flex flex-wrap gap-1.5">
                  {placeholders.map(p => (
                    <button key={p} onClick={()=>insertPlaceholder(p)} className="px-2 py-1 text-[10px] font-mono bg-zinc-800 hover:bg-blue-600 border border-zinc-700 rounded text-zinc-300 hover:text-white transition-colors">{p}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
              <button onClick={()=>setShowForm(false)} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
              <button onClick={save} data-testid="template-save-btn" className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">Speichern</button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-2xl">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Vorschau · {preview.kind}</div>
                <h3 className="font-display font-semibold text-zinc-50 mt-1">{preview.name}</h3>
              </div>
              <button onClick={()=>setPreview(null)} className="text-zinc-400">✕</button>
            </div>
            <div className="p-5 space-y-3">
              {preview.subject && <div className="bg-zinc-950 border border-zinc-800 rounded p-3 text-sm text-zinc-100"><span className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Betreff</span>{preview.subject}</div>}
              <pre className="bg-zinc-950 border border-zinc-800 rounded p-3 text-sm text-zinc-100 whitespace-pre-wrap font-mono">{preview.body}</pre>
              <div className="text-xs text-zinc-500">Platzhalter wurden mit Platzhalter-Werten bzw. leeren Strings ersetzt. Im echten Versand werden die realen Daten aus Rechnung / Mitarbeiter / Fahrzeug / Kunde gezogen.</div>
            </div>
            <div className="p-5 border-t border-zinc-800 flex justify-end">
              <button onClick={()=>setPreview(null)} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Schließen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function ApisAdmin() {
  const [slots, setSlots] = useState([]);
  const [recipients, setRecipients] = useState("");
  const [drafts, setDrafts] = useState({});
  const [testMail, setTestMail] = useState("");
  const [sending, setSending] = useState(false);
  const [scan, setScan] = useState(null);
  const [days, setDays] = useState(30);
  const [schedule, setSchedule] = useState(null);

  async function load() {
    try {
      const { data } = await api.get("/settings/api-keys");
      setSlots(data.slots || []);
      setRecipients(data.alert_recipients || "");
    } catch { toast.error("Konnte API-Keys nicht laden"); }
  }
  async function loadSchedule() {
    try { const { data } = await api.get("/alerts/schedule"); setSchedule(data); } catch {}
  }
  useEffect(() => { load(); loadSchedule(); }, []);
  useEffect(() => {
    api.get("/alerts/scan", { params: { days } }).then(({ data }) => setScan(data)).catch(() => {});
  }, [days]);

  async function saveSlot(name) {
    const v = drafts[name];
    if (v === undefined) return;
    try {
      await api.put(`/settings/api-keys/${name}`, { value: v });
      toast.success("Gespeichert");
      setDrafts((d) => { const c = { ...d }; delete c[name]; return c; });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }
  async function clearSlot(name) {
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    await api.delete(`/settings/api-keys/${name}`);
    toast.success("Gelöscht"); load();
  }
  async function saveRecipients() {
    try { await api.put("/settings/alert-recipients", { recipients }); toast.success("Empfänger gespeichert"); }
    catch { toast.error("Fehler"); }
  }
  async function sendTest() {
    if (!testMail) { toast.error("E-Mail-Adresse eingeben"); return; }
    setSending(true);
    try { await api.post("/alerts/test-email", { to: testMail }); toast.success(`Test-E-Mail gesendet an ${testMail}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Versand fehlgeschlagen"); }
    finally { setSending(false); }
  }
  async function sendExpiry() {
    if (!window.confirm("Ablauf-Alarm-Mail jetzt an alle Empfänger senden?")) return;
    setSending(true);
    try {
      const { data } = await api.post("/alerts/send-expiry", { days });
      const ok = data.results.filter((r) => r.ok).length;
      const fail = data.results.length - ok;
      toast.success(`Gesendet: ${ok} · Fehlgeschlagen: ${fail}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Versand fehlgeschlagen"); }
    finally { setSending(false); }
  }
  async function runCronNow() {
    setSending(true);
    try {
      const { data } = await api.post("/alerts/run-now");
      if (data.skipped) toast.info(`Cron übersprungen: ${data.reason}`);
      else toast.success(`Cron ausgeführt · ${data.sent || 0}/${data.total || 0} gesendet`);
      loadSchedule();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setSending(false); }
  }

  const totalExpiring = scan ? (scan.expired_docs.length + scan.expiring_docs.length + scan.expiring_pickerl.length + scan.expiring_insurance.length) : 0;

  return (
    <div className="space-y-6" data-testid="apis-admin">
      <div className="bg-zinc-900 border border-zinc-800 rounded p-6">
        <h3 className="font-display text-zinc-50 font-semibold mb-1">API-Schlüssel</h3>
        <p className="text-xs text-zinc-500 mb-5">Diese Schlüssel werden verschlüsselt in der Datenbank gespeichert. Nur Admin / Geschäftsführung haben Zugriff.</p>
        <div className="space-y-4">
          {slots.map((s) => (
            <div key={s.name} className="border border-zinc-800 rounded p-4" data-testid={`api-slot-${s.name}`}>
              <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                <div>
                  <div className="text-sm font-medium text-zinc-100">{s.label}</div>
                  <div className="text-xs text-zinc-500">{s.description}</div>
                </div>
                <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold rounded border ${s.configured ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-zinc-800 text-zinc-400 border-zinc-700"}`}>
                  {s.configured ? "Aktiv" : "Nicht gesetzt"}
                </span>
              </div>
              {s.configured && (
                <div className="text-[11px] font-mono text-zinc-400 mb-2">Aktueller Wert: {s.preview || "—"}</div>
              )}
              <div className="flex gap-2 mt-2">
                <input
                  type={s.sensitive ? "password" : "text"}
                  placeholder={s.configured ? "Neuen Wert eingeben, um zu ersetzen…" : "Wert eingeben…"}
                  value={drafts[s.name] ?? ""}
                  onChange={(e) => setDrafts({ ...drafts, [s.name]: e.target.value })}
                  className="flex-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"
                  data-testid={`api-input-${s.name}`}
                />
                <button
                  onClick={() => saveSlot(s.name)}
                  disabled={drafts[s.name] === undefined || drafts[s.name] === ""}
                  data-testid={`api-save-${s.name}`}
                  className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 disabled:text-zinc-500 text-white rounded font-medium">
                  Speichern
                </button>
                {s.configured && (
                  <button onClick={() => clearSlot(s.name)} className="px-3 py-2 text-sm bg-zinc-800 hover:bg-red-600/80 text-zinc-200 rounded">
                    Entfernen
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded p-6">
        <h3 className="font-display text-zinc-50 font-semibold mb-1">Alarm-Empfänger</h3>
        <p className="text-xs text-zinc-500 mb-4">Kommagetrennte Liste von E-Mail-Adressen, die Ablauf-Alarme (§57a, Versicherung, Dokumente) erhalten.</p>
        <div className="flex gap-2">
          <input
            value={recipients}
            onChange={(e) => setRecipients(e.target.value)}
            placeholder="office@munjatrans.at, fuhrpark@munjatrans.at"
            data-testid="alert-recipients-input"
            className="flex-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"
          />
          <button onClick={saveRecipients} data-testid="alert-recipients-save" className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">Speichern</button>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded p-6">
        <h3 className="font-display text-zinc-50 font-semibold mb-1">E-Mail testen</h3>
        <p className="text-xs text-zinc-500 mb-4">Sende eine Test-E-Mail, um die Resend-Integration zu prüfen.</p>
        <div className="flex gap-2">
          <input
            type="email"
            value={testMail}
            onChange={(e) => setTestMail(e.target.value)}
            placeholder="test@example.com"
            data-testid="test-email-input"
            className="flex-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"
          />
          <button onClick={sendTest} disabled={sending} data-testid="test-email-send" className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-800 disabled:text-zinc-500 text-white rounded font-medium">
            {sending ? "Sende…" : "Test senden"}
          </button>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded p-6">
        <h3 className="font-display text-zinc-50 font-semibold mb-1">Ablauf-Alarm – Vorschau & Versand</h3>
        <p className="text-xs text-zinc-500 mb-4">Scannt Dokumente, §57a-Pickerl und Versicherungen im gewählten Zeitfenster.</p>
        <div className="flex items-center gap-3 mb-4">
          <label className="text-xs text-zinc-400 uppercase tracking-wider">Fenster (Tage)</label>
          <input type="number" min={1} max={365} value={days} onChange={(e)=>setDays(parseInt(e.target.value)||30)} className="w-24 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100" data-testid="expiry-days-input"/>
          <button onClick={sendExpiry} disabled={sending || totalExpiring === 0} data-testid="send-expiry-btn" className="ml-auto px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 disabled:bg-zinc-800 disabled:text-zinc-500 text-white rounded font-medium">
            Jetzt senden ({totalExpiring})
          </button>
        </div>
        {scan && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Stat label="Abgelaufen" value={scan.expired_docs.length} tone="red"/>
            <Stat label="Dok. laufen ab" value={scan.expiring_docs.length} tone="amber"/>
            <Stat label="§57a / Pickerl" value={scan.expiring_pickerl.length} tone="amber"/>
            <Stat label="Versicherung" value={scan.expiring_insurance.length} tone="amber"/>
          </div>
        )}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded p-6" data-testid="portal-auto-disable-panel">
        <h3 className="font-display text-zinc-50 font-semibold mb-1 flex items-center gap-2">
          Portal Auto-Disable
        </h3>
        <p className="text-xs text-zinc-500 mb-4">
          Deaktiviert automatisch Fahrer-Portal-Konten, die seit X Tagen nicht mehr genutzt wurden.
          Aktivierung ist danach nur durch einen Admin im Mitarbeiter-Profil möglich. Schutz vor verlorenen Zugangsdaten.
        </p>
        <PortalAutoDisablePanel />
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded p-6" data-testid="cron-panel">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display text-zinc-50 font-semibold mb-1">Automatischer Täglicher Versand</h3>
            <p className="text-xs text-zinc-500">Cron-Job läuft automatisch im Hintergrund. Sendet Ablauf-Alarme ohne manuelle Auslösung.</p>
          </div>
          <button onClick={runCronNow} disabled={sending} data-testid="cron-run-now" className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 disabled:text-zinc-500 text-white rounded font-medium">
            Jetzt ausführen (Test)
          </button>
        </div>
        {schedule && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="border border-zinc-800 rounded p-4">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Zeitplan</div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`h-2 w-2 rounded-full ${schedule.enabled ? "bg-emerald-500" : "bg-zinc-600"}`}></span>
                <span className="text-zinc-100 font-medium">{schedule.enabled ? "Aktiv" : "Deaktiviert"}</span>
              </div>
              <div className="text-xs text-zinc-400">Täglich <span className="font-mono text-zinc-200">{String(schedule.hour).padStart(2,"0")}:{String(schedule.minute).padStart(2,"0")}</span> · {schedule.timezone}</div>
              {schedule.next_run_at && (
                <div className="text-xs text-zinc-400 mt-1">Nächster Lauf: <span className="font-mono text-zinc-200">{new Date(schedule.next_run_at).toLocaleString("de-AT")}</span></div>
              )}
            </div>
            <div className="border border-zinc-800 rounded p-4">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Letzter Lauf</div>
              {schedule.last_run_at ? (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold rounded border ${
                      schedule.last_status === "ok" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" :
                      schedule.last_status === "ok_empty" ? "bg-blue-500/10 text-blue-400 border-blue-500/20" :
                      schedule.last_status === "skipped" ? "bg-zinc-800 text-zinc-400 border-zinc-700" :
                      schedule.last_status === "partial" ? "bg-amber-500/10 text-amber-400 border-amber-500/20" :
                      "bg-red-500/10 text-red-400 border-red-500/20"
                    }`}>{schedule.last_status || "—"}</span>
                    <span className="text-xs text-zinc-400 font-mono">{new Date(schedule.last_run_at).toLocaleString("de-AT")}</span>
                  </div>
                  {schedule.last_message && <div className="text-xs text-zinc-300 mt-1">{schedule.last_message}</div>}
                </>
              ) : (
                <div className="text-xs text-zinc-500 italic">Noch nicht ausgeführt</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const colors = { red: "text-red-400 border-red-500/30 bg-red-500/5", amber: "text-amber-400 border-amber-500/30 bg-amber-500/5" };
  return (
    <div className={`border rounded p-3 ${colors[tone] || "border-zinc-700 bg-zinc-900"}`}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-2xl font-mono font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}


// ============================================================
// FeatureFlagsAdmin — Toggle-Zentrale für alle Power-Features
// ============================================================
function FeatureFlagsAdmin() {
  const [flags, setFlags] = useState([]);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });
  const [savingKey, setSavingKey] = useState(null);

  // Phase 5Z.36 — All feature flags are intentionally locked behind the
  // "Erweiterungspaket" upsell. Until the paid add-on launches, nothing here
  // is interactive. Flip this single constant back to `false` to restore
  // toggles and per-flag coming_soon handling.
  const ALL_LOCKED = true;

  async function load() {
    try {
      const { data } = await api.get("/settings/feature-flags");
      setFlags(data.flags || []);
      setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Laden"); }
  }
  useEffect(() => { load(); }, []);

  async function toggle(flag) {
    if (ALL_LOCKED) {
      toast.info("Erweiterungspaket — bald verfügbar zum Erwerb. Diese Funktion wird mit dem Add-On freigeschaltet.");
      return;
    }
    if (flag.coming_soon) {
      toast.info("Diese Erweiterung ist noch in Entwicklung — bald verfügbar.");
      return;
    }
    setSavingKey(flag.key);
    try {
      const { data } = await api.post(`/settings/feature-flags/${flag.key}/toggle`);
      setFlags(prev => prev.map(f => f.key === flag.key ? { ...f, enabled: data.enabled } : f));
      toast.success(`„${flag.title}" ${data.enabled ? "aktiviert" : "deaktiviert"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setSavingKey(null); }
  }

  // Group by category
  const grouped = {};
  for (const f of flags) {
    const cat = f.category || "Allgemein";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(f);
  }
  const categoryOrder = ["RBAC & Sicherheit", "Automatisierung", "Fuhrpark", "Compliance", "Benachrichtigungen", "Finanzen", "UI", "Allgemein"];
  const sortedCats = Object.keys(grouped).sort((a, b) => {
    const ai = categoryOrder.indexOf(a), bi = categoryOrder.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const totalLive = ALL_LOCKED ? flags.length : flags.filter(f => !f.coming_soon).length;

  return (
    <div data-testid="feature-flags-admin">
      {/* Header card */}
      <div className="bg-gradient-to-br from-amber-950/30 to-zinc-900 border border-amber-900/40 rounded-md p-5 mb-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-display font-semibold text-lg text-zinc-50 inline-flex items-center gap-2">
              <svg className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/></svg>
              Erweiterungspaket — bald verfügbar
            </h3>
            <p className="text-sm text-zinc-400 mt-1 max-w-2xl" data-testid="extensions-locked-banner">
              Alle Power-Features sind aktuell als kostenpflichtiges <strong className="text-amber-300">Erweiterungspaket</strong> in Vorbereitung
              und können in Kürze einzeln oder im Bundle erworben werden. Bis zum Release sind sämtliche Schalter gesperrt.
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-3xl font-bold text-amber-400 font-mono">{totalLive}</div>
            <div className="text-[10px] uppercase tracking-wider text-amber-500/80 mt-1">in Vorbereitung</div>
          </div>
        </div>
        {meta.updated_at && !ALL_LOCKED && (
          <div className="mt-3 pt-3 border-t border-amber-900/30 text-[11px] text-zinc-500">
            Zuletzt geändert: <span className="font-mono">{fmtDateTime(meta.updated_at)}</span>
            {meta.updated_by && <> von <span className="text-zinc-300">{meta.updated_by}</span></>}
          </div>
        )}
      </div>

      {/* Grouped flag list */}
      {sortedCats.map(cat => (
        <div key={cat} className="mb-6">
          <h4 className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2 px-1">{cat}</h4>
          <div className="space-y-2">
            {grouped[cat].map(flag => {
              const locked = ALL_LOCKED || flag.coming_soon;
              return (
                <div key={flag.key} data-testid={`flag-card-${flag.key}`}
                  className={`bg-zinc-900 border rounded-md p-4 transition-colors ${
                    !ALL_LOCKED && flag.enabled ? "border-emerald-500/30" : "border-zinc-800"
                  } ${locked ? "opacity-60 grayscale" : ""}`}>
                  <div className="flex items-start gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-semibold text-zinc-200">{flag.title}</span>
                        {ALL_LOCKED ? (
                          <span className="text-[9px] uppercase tracking-wider font-semibold text-amber-400 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded">
                            Bald verfügbar
                          </span>
                        ) : flag.coming_soon ? (
                          <span className="text-[9px] uppercase tracking-wider font-semibold text-amber-400 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded">Bald verfügbar</span>
                        ) : flag.enabled ? (
                          <span className="text-[9px] uppercase tracking-wider font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-1.5 py-0.5 rounded">Aktiv</span>
                        ) : null}
                      </div>
                      <p className="text-xs text-zinc-500 leading-relaxed">{flag.description}</p>
                      <div className="mt-1.5 text-[10px] font-mono text-zinc-600">{flag.key}</div>
                    </div>
                    <button
                      onClick={() => toggle(flag)}
                      disabled={savingKey === flag.key || locked}
                      data-testid={`flag-toggle-${flag.key}`}
                      aria-pressed={!ALL_LOCKED && flag.enabled}
                      title={ALL_LOCKED ? "Bald verfügbar — Erweiterungspaket in Vorbereitung" : undefined}
                      className={`shrink-0 relative inline-flex h-7 w-12 items-center rounded-full transition-colors bg-zinc-800 ${
                        locked ? "cursor-not-allowed" : "cursor-pointer"
                      } ${!ALL_LOCKED && flag.enabled ? "bg-emerald-600" : ""}`}
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-zinc-500 transition-transform ${
                        !ALL_LOCKED && flag.enabled ? "translate-x-6 bg-white" : "translate-x-1"
                      }`}/>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}



// ============================================================
// TemplatesHub — sub-tabs for 4 kinds of templates
// ============================================================
function TemplatesHub() {
  const [sub, setSub] = useState("Schichtplan");
  const subs = ["Schichtplan", "Abwesenheit", "Bonus", "E-Mail / PDF"];
  return (
    <div data-testid="templates-hub">
      <div className="flex gap-2 mb-5 flex-wrap">
        {subs.map(s => (
          <button key={s} onClick={() => setSub(s)} data-testid={`templates-subtab-${s}`}
            className={`px-3 py-1.5 text-xs font-medium rounded border transition-colors ${sub === s ? "bg-blue-600 text-white border-blue-500" : "bg-zinc-900 text-zinc-300 border-zinc-800 hover:bg-zinc-800"}`}>
            {s}
          </button>
        ))}
      </div>
      {sub === "Schichtplan" && <ShiftTemplatesAdmin/>}
      {sub === "Abwesenheit" && <AbsenceTemplatesAdmin/>}
      {sub === "Bonus" && <BonusTemplatesAdmin/>}
      {sub === "E-Mail / PDF" && <TemplatesAdmin/>}
    </div>
  );
}

// ============================================================
// ShiftTemplatesAdmin — admin builds the shift presets
// ============================================================
function ShiftTemplatesAdmin() {
  const EMPTY = { name: "", department_id: "", shift_type: "arbeit", einsatzort_id: "", workplace: "", start_time: "07:00", duration_minutes: 510, break_minutes: 30, color: "#3b82f6", notes: "" };
  const [items, setItems] = useState([]);
  const [depts, setDepts] = useState([]);
  const [workplaces, setWorkplaces] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filterDept, setFilterDept] = useState("");

  async function load() {
    const [t, d, w] = await Promise.all([
      api.get("/shift-templates", { params: filterDept ? { department_id: filterDept } : {} }),
      api.get("/departments"),
      api.get("/settings/workplaces"),
    ]);
    setItems(t.data); setDepts(d.data); setWorkplaces(w.data);
  }
  useEffect(() => { load(); }, [filterDept]);

  async function save() {
    if (!form.name.trim()) { toast.error("Name ist Pflicht"); return; }
    if (!form.department_id) { toast.error("Abteilung ist Pflicht"); return; }
    try {
      if (editing) await api.put(`/shift-templates/${editing.id}`, form);
      else await api.post("/shift-templates", form);
      toast.success("Gespeichert"); setShowForm(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }
  async function remove(id) {
    if (!window.confirm("Vorlage löschen?")) return;
    await api.delete(`/shift-templates/${id}`); toast.success("Gelöscht"); load();
  }
  function startEdit(t) { setEditing(t); setForm({ ...EMPTY, ...t }); setShowForm(true); }
  function startNew() { setEditing(null); setForm(EMPTY); setShowForm(true); }

  const endTime = (t) => {
    const [h, m] = (t.start_time || "07:00").split(":").map(n => parseInt(n, 10));
    const total = h * 60 + m + (t.duration_minutes || 0);
    return `${String(Math.floor((total % 1440) / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
  };
  const deptName = (id) => depts.find(d => d.id === id)?.name || "—";
  const deptColor = (id) => depts.find(d => d.id === id)?.color || "#3b82f6";

  return (
    <div data-testid="shift-templates-admin">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <select value={filterDept} onChange={(e) => setFilterDept(e.target.value)}
            data-testid="shift-templates-filter"
            className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
            <option value="">Alle Abteilungen</option>
            {depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <span className="text-xs text-zinc-500">{items.length} Vorlage(n)</span>
        </div>
        <button onClick={startNew} data-testid="shift-template-new"
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded">
          <span>+</span> Neue Schichtplan-Vorlage
        </button>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded overflow-hidden">
        {items.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">
            Noch keine Schichtplan-Vorlagen. Lege die erste an — z.B. <span className="text-zinc-300">„Amazon DVI3 ab 10:00 · 8:30h · 30 min Pause"</span>.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Abteilung</th>
              <th className="px-4 py-3 text-left">Einsatzort</th>
              <th className="px-4 py-3 text-left">Zeit</th>
              <th className="px-4 py-3 text-right">Netto</th>
              <th className="px-4 py-3 text-right">Aktionen</th>
            </tr></thead>
            <tbody>{items.map(t => (
              <tr key={t.id} data-testid={`shift-template-row-${t.id}`} className="border-b border-zinc-800 hover:bg-zinc-800/40">
                <td className="px-4 py-2.5 text-zinc-100 font-medium">
                  <div className="flex items-center gap-2">
                    <span className="w-1 h-5 rounded" style={{ backgroundColor: t.color || "#3b82f6" }}/>
                    {t.name}
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  <span className="text-[10px] px-2 py-0.5 rounded text-white" style={{ backgroundColor: deptColor(t.department_id) }}>
                    {deptName(t.department_id)}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-zinc-300">{t.einsatzort_name || t.workplace || "—"}</td>
                <td className="px-4 py-2.5 text-zinc-300 font-mono">{t.start_time} – {endTime(t)}</td>
                <td className="px-4 py-2.5 text-right font-mono tabular-nums text-zinc-300">
                  {Math.floor(((t.duration_minutes || 0) - (t.break_minutes || 0)) / 60)}h{((t.duration_minutes || 0) - (t.break_minutes || 0)) % 60 ? `${((t.duration_minutes || 0) - (t.break_minutes || 0)) % 60}m` : ""}
                  <div className="text-[10px] text-zinc-500">inkl. {t.break_minutes || 0}m Pause</div>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={() => startEdit(t)} data-testid={`shift-template-edit-${t.id}`} className="text-xs text-blue-400 hover:text-blue-300 mr-3">Bearbeiten</button>
                  <button onClick={() => remove(t.id)} className="text-xs text-red-400 hover:text-red-300">Löschen</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="shift-template-form">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-xl">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="font-display font-semibold text-zinc-50">{editing ? "Vorlage bearbeiten" : "Neue Schichtplan-Vorlage"}</h3>
              <button onClick={() => setShowForm(false)} className="text-zinc-400">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[10px] uppercase text-zinc-500">Name *</label>
                <input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})}
                  placeholder="z.B. Amazon DVI3 ab 10:00"
                  data-testid="shift-template-name"
                  className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
              </div>
              <div>
                <label className="text-[10px] uppercase text-zinc-500">Abteilung *</label>
                <select value={form.department_id} onChange={(e)=>setForm({...form, department_id: e.target.value})}
                  data-testid="shift-template-dept"
                  className={`w-full mt-1 px-3 py-2 bg-zinc-950 border rounded text-sm text-zinc-100 ${!form.department_id ? "border-amber-500/60 ring-1 ring-amber-500/30" : "border-zinc-800"}`}>
                  <option value="">— Abteilung wählen —</option>
                  {depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase text-zinc-500">Schicht-Typ</label>
                  <select value={form.shift_type} onChange={(e)=>setForm({...form, shift_type: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                    <option value="arbeit">Arbeit</option><option value="frei">Frei</option>
                    <option value="urlaub">Urlaub</option><option value="krank">Krank</option>
                    <option value="schulung">Schulung</option><option value="buero">Büro</option><option value="homeoffice">Homeoffice</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-zinc-500">Einsatzort</label>
                  <select
                    value={form.einsatzort_id || ""}
                    onChange={(e) => {
                      const id = e.target.value;
                      const wp = workplaces.find((w) => w.id === id);
                      setForm({ ...form, einsatzort_id: id, workplace: wp ? wp.name : form.workplace });
                    }}
                    data-testid="shift-template-einsatzort"
                    className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                    <option value="">— Einsatzort wählen —</option>
                    {workplaces.map((w) => (
                      <option key={w.id} value={w.id}>{w.name}{w.city ? ` · ${w.city}` : ""}</option>
                    ))}
                  </select>
                  <div className="text-[10px] text-zinc-500 mt-1">
                    Verknüpft mit „Einsatzorte". Neue Orte unter Einstellungen → Einsatzorte anlegen.
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] uppercase text-zinc-500">Startzeit</label>
                  <input type="time" value={form.start_time} onChange={(e)=>setForm({...form, start_time: e.target.value})}
                    data-testid="shift-template-start"
                    className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-zinc-500">Dauer (Min)</label>
                  <input type="number" value={form.duration_minutes} onChange={(e)=>setForm({...form, duration_minutes: +e.target.value})}
                    data-testid="shift-template-duration"
                    className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
                  <div className="text-[10px] text-zinc-500 mt-1">= {Math.floor(form.duration_minutes/60)}h{form.duration_minutes%60 ? `${form.duration_minutes%60}m` : ""}</div>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-zinc-500">Pause (Min)</label>
                  <input type="number" value={form.break_minutes} onChange={(e)=>setForm({...form, break_minutes: +e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
                </div>
              </div>
              <div className="grid grid-cols-[auto_1fr] gap-3 items-end">
                <div>
                  <label className="text-[10px] uppercase text-zinc-500">Farbe</label>
                  <input type="color" value={form.color} onChange={(e)=>setForm({...form, color: e.target.value})}
                    className="mt-1 w-16 h-10 bg-zinc-950 border border-zinc-800 rounded cursor-pointer"/>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-zinc-500">Notiz (optional)</label>
                  <input value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
                </div>
              </div>
              {/* Preview */}
              <div className="bg-zinc-950 border border-zinc-800 rounded p-3 text-xs text-zinc-300">
                <div className="text-[10px] uppercase text-zinc-500 mb-1">Vorschau</div>
                <div><span className="font-semibold" style={{ color: form.color }}>{form.name || "Ohne Name"}</span></div>
                <div className="font-mono text-zinc-400">
                  {form.start_time} – {(() => {
                    const [h, m] = (form.start_time || "07:00").split(":").map(n=>parseInt(n,10));
                    const total = h*60+m+(form.duration_minutes||0);
                    return `${String(Math.floor((total%1440)/60)).padStart(2,"0")}:${String(total%60).padStart(2,"0")}`;
                  })()}
                  {" · "}Netto {Math.floor((form.duration_minutes-form.break_minutes)/60)}h{(form.duration_minutes-form.break_minutes)%60 ? `${(form.duration_minutes-form.break_minutes)%60}m` : ""} (inkl. {form.break_minutes}m Pause)
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
              <button onClick={()=>setShowForm(false)} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
              <button onClick={save} data-testid="shift-template-save" className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-medium">Speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// AbsenceTemplatesAdmin — presets for sick/vacation
// ============================================================
function AbsenceTemplatesAdmin() {
  const EMPTY = { name: "", shift_type: "frei", notes_template: "", color: "#f59e0b" };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  async function load() { const { data } = await api.get("/absence-templates"); setItems(data); }
  useEffect(() => { load(); }, []);
  async function save() {
    if (!form.name.trim()) { toast.error("Name ist Pflicht"); return; }
    try {
      if (editing) await api.put(`/absence-templates/${editing.id}`, form);
      else await api.post("/absence-templates", form);
      toast.success("Gespeichert"); setShowForm(false); setEditing(null); setForm(EMPTY); load();
    } catch { toast.error("Fehler"); }
  }
  async function remove(id) {
    if (!window.confirm("Vorlage löschen?")) return;
    await api.delete(`/absence-templates/${id}`); toast.success("Gelöscht"); load();
  }
  async function seedDefaults() {
    const presets = [
      { name: "Frei", shift_type: "frei", notes_template: "", color: "#71717a" },
      { name: "Urlaub", shift_type: "urlaub", notes_template: "", color: "#10b981" },
      { name: "Zeitausgleich", shift_type: "zeitausgleich", notes_template: "", color: "#06b6d4" },
      { name: "Krank", shift_type: "krank", notes_template: "", color: "#ef4444" },
      { name: "Schulung", shift_type: "schulung", notes_template: "", color: "#f59e0b" },
      { name: "Feiertag", shift_type: "feiertag", notes_template: "", color: "#ec4899" },
      { name: "Sonderurlaub", shift_type: "sonderurlaub", notes_template: "", color: "#a855f7" },
    ];
    try {
      for (const p of presets) {
        await api.post("/absence-templates", p);
      }
      toast.success(`${presets.length} Standard-Vorlagen angelegt`);
      load();
    } catch { toast.error("Fehler beim Anlegen der Standard-Vorlagen"); }
  }
  return (
    <div data-testid="absence-templates-admin">
      <div className="flex justify-between items-center mb-3 gap-2 flex-wrap">
        <div className="text-[11px] text-zinc-500">
          Definiere hier die Abwesenheitsarten (Frei, Urlaub, Zeitausgleich, Krank, …), die im Dienstplan per Klick zugewiesen werden können.
        </div>
        <div className="flex gap-2">
          {items.length === 0 && (
            <button onClick={seedDefaults} data-testid="absence-seed-defaults" className="px-3 py-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded">
              Standard-Vorlagen anlegen
            </button>
          )}
          <button onClick={()=>{setEditing(null);setForm(EMPTY);setShowForm(true);}} data-testid="absence-tpl-new" className="px-3 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded">+ Neue Abwesenheits-Vorlage</button>
        </div>
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded overflow-hidden">
        {items.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">
            Noch keine Vorlagen.<br/>
            <span className="text-xs text-zinc-600">Tipp: Klick auf <span className="text-emerald-400 font-medium">„Standard-Vorlagen anlegen"</span> für einen Schnellstart (Frei, Urlaub, Zeitausgleich, Krank, …).</span>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-3 text-left">Name</th><th className="px-4 py-3 text-left">Typ</th><th className="px-4 py-3 text-left">Notiz</th><th className="px-4 py-3 text-right">Aktionen</th>
            </tr></thead>
            <tbody>{items.map(t => (
              <tr key={t.id} className="border-b border-zinc-800 hover:bg-zinc-800/40">
                <td className="px-4 py-2.5 text-zinc-100"><span className="w-1 h-5 rounded mr-2 inline-block" style={{backgroundColor: t.color || "#f59e0b"}}/>{t.name}</td>
                <td className="px-4 py-2.5 text-zinc-300 capitalize">{t.shift_type}</td>
                <td className="px-4 py-2.5 text-zinc-400 text-xs">{t.notes_template || "—"}</td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={()=>{setEditing(t);setForm({...EMPTY,...t});setShowForm(true);}} className="text-xs text-blue-400 hover:text-blue-300 mr-3">Bearbeiten</button>
                  <button onClick={()=>remove(t.id)} className="text-xs text-red-400 hover:text-red-300">Löschen</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-md">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between"><h3 className="font-display font-semibold text-zinc-50">{editing?"Bearbeiten":"Neue Vorlage"}</h3><button onClick={()=>setShowForm(false)} className="text-zinc-400">✕</button></div>
            <div className="p-5 space-y-3">
              <input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} placeholder="Name, z.B. Standard-Krankmeldung" className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
              <select value={form.shift_type} onChange={(e)=>setForm({...form,shift_type:e.target.value})} data-testid="absence-tpl-type" className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                <option value="frei">Frei</option>
                <option value="urlaub">Urlaub</option>
                <option value="zeitausgleich">Zeitausgleich</option>
                <option value="krank">Krank / Krankenstand</option>
                <option value="schulung">Schulung</option>
                <option value="sonderurlaub">Sonderurlaub</option>
                <option value="feiertag">Feiertag</option>
                <option value="unbezahlt_frei">Unbezahlt frei</option>
                <option value="karenz">Karenz</option>
                <option value="unentschuldigt">Unentschuldigt</option>
              </select>
              <textarea value={form.notes_template} onChange={(e)=>setForm({...form,notes_template:e.target.value})} placeholder="Standard-Notiz" rows={3} className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
              <input type="color" value={form.color} onChange={(e)=>setForm({...form,color:e.target.value})} className="w-16 h-10 bg-zinc-950 border border-zinc-800 rounded"/>
            </div>
            <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
              <button onClick={()=>setShowForm(false)} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
              <button onClick={save} className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded">Speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// BonusTemplatesAdmin — bonus/malus presets
// ============================================================
function BonusTemplatesAdmin() {
  const EMPTY = { name: "", type: "bonus", amount: 0, notes_template: "" };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  async function load() { const { data } = await api.get("/bonus-templates"); setItems(data); }
  useEffect(() => { load(); }, []);
  async function save() {
    if (!form.name.trim()) { toast.error("Name ist Pflicht"); return; }
    try {
      if (editing) await api.put(`/bonus-templates/${editing.id}`, form);
      else await api.post("/bonus-templates", form);
      toast.success("Gespeichert"); setShowForm(false); setEditing(null); setForm(EMPTY); load();
    } catch { toast.error("Fehler"); }
  }
  async function remove(id) {
    if (!window.confirm("Vorlage löschen?")) return;
    await api.delete(`/bonus-templates/${id}`); toast.success("Gelöscht"); load();
  }
  return (
    <div data-testid="bonus-templates-admin">
      <div className="flex justify-end mb-3">
        <button onClick={()=>{setEditing(null);setForm(EMPTY);setShowForm(true);}} className="px-3 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded">+ Neue Bonus-Vorlage</button>
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded overflow-hidden">
        {items.length === 0 ? <EmptyState label="Noch keine Vorlagen" /> : (
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-3 text-left">Name</th><th className="px-4 py-3 text-left">Typ</th><th className="px-4 py-3 text-right">Betrag</th><th className="px-4 py-3 text-right">Aktionen</th>
            </tr></thead>
            <tbody>{items.map(t => (
              <tr key={t.id} className="border-b border-zinc-800 hover:bg-zinc-800/40">
                <td className="px-4 py-2.5 text-zinc-100">{t.name}</td>
                <td className="px-4 py-2.5 text-zinc-300 capitalize">{t.type}</td>
                <td className="px-4 py-2.5 text-right font-mono tabular-nums">{fmtCur(t.amount)}</td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={()=>{setEditing(t);setForm({...EMPTY,...t});setShowForm(true);}} className="text-xs text-blue-400 hover:text-blue-300 mr-3">Bearbeiten</button>
                  <button onClick={()=>remove(t.id)} className="text-xs text-red-400 hover:text-red-300">Löschen</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-md">
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between"><h3 className="font-display font-semibold text-zinc-50">{editing?"Bearbeiten":"Neue Vorlage"}</h3><button onClick={()=>setShowForm(false)} className="text-zinc-400">✕</button></div>
            <div className="p-5 space-y-3">
              <input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} placeholder="Name, z.B. Trinkgeld-Prämie" className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
              <select value={form.type} onChange={(e)=>setForm({...form,type:e.target.value})} className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                <option value="bonus">Bonus</option><option value="abzug">Abzug</option><option value="strafe">Strafe</option>
              </select>
              <input type="number" step="0.01" value={form.amount} onChange={(e)=>setForm({...form,amount:+e.target.value})} placeholder="Betrag" className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
              <input value={form.notes_template} onChange={(e)=>setForm({...form,notes_template:e.target.value})} placeholder="Standard-Notiz" className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            </div>
            <div className="p-5 border-t border-zinc-800 flex justify-end gap-2">
              <button onClick={()=>setShowForm(false)} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
              <button onClick={save} className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded">Speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function LenkerauskunftConfig() {
  const [cfg, setCfg] = useState({ sender_email: "", sender_name: "", default_cc: [], updated_at: "", updated_by: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [newCc, setNewCc] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/lenkerauskunft");
      setCfg({
        sender_email: data.sender_email || "",
        sender_name: data.sender_name || "",
        default_cc: data.default_cc || [],
        updated_at: data.updated_at || "",
        updated_by: data.updated_by || "",
      });
      setDirty(false);
    } catch { toast.error("Konfiguration konnte nicht geladen werden"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function update(field, value) {
    setCfg(c => ({ ...c, [field]: value }));
    setDirty(true);
  }

  function addCc() {
    const v = (newCc || "").trim();
    if (!v || !v.includes("@")) { toast.error("Ungültige E-Mail"); return; }
    if (cfg.default_cc.map(x => x.toLowerCase()).includes(v.toLowerCase())) {
      toast.info("Bereits in Liste"); return;
    }
    update("default_cc", [...cfg.default_cc, v]);
    setNewCc("");
  }
  function removeCc(email) {
    update("default_cc", cfg.default_cc.filter(e => e !== email));
  }

  async function save() {
    if (cfg.sender_email && !cfg.sender_email.includes("@")) {
      toast.error("Bitte eine gültige E-Mail-Adresse eingeben"); return;
    }
    setSaving(true);
    try {
      await api.put("/settings/lenkerauskunft", {
        sender_email: cfg.sender_email,
        sender_name: cfg.sender_name,
        default_cc: cfg.default_cc,
      });
      toast.success("Einstellungen gespeichert");
      setDirty(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  }

  if (loading) return <div className="p-6 text-zinc-400 text-sm">Lade…</div>;

  return (
    <div className="space-y-4" data-testid="lenkerauskunft-config">
      <div className="bg-zinc-900 border border-zinc-800 rounded p-6">
        <h3 className="font-display text-zinc-50 font-semibold mb-1">Lenkerauskunft-Versand</h3>
        <p className="text-xs text-zinc-500 mb-5">
          Konfiguriert Absender und Standard-Kopie-Empfänger für Lenkerauskünfte an Behörden.
        </p>

        <div className="grid grid-cols-2 gap-4 mb-5">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-zinc-500 block mb-1">Absender-E-Mail</label>
            <input value={cfg.sender_email || ""}
              onChange={(e) => update("sender_email", e.target.value)}
              placeholder="lenkerauskunft@firma.at"
              data-testid="la-cfg-sender-email"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            <div className="text-[10px] text-zinc-500 mt-1">
              Muss bei Ihrem E-Mail-Provider (z.B. Resend) verifiziert sein.
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-zinc-500 block mb-1">Absender-Name</label>
            <input value={cfg.sender_name || ""}
              onChange={(e) => update("sender_name", e.target.value)}
              placeholder="Firma — Rechtsabteilung"
              data-testid="la-cfg-sender-name"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
          </div>
        </div>

        <div className="bg-zinc-950 border border-zinc-800 rounded p-4 mb-5">
          <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2">Standard-Kopie-Empfänger</div>
          <div className="flex flex-wrap gap-1.5 items-center">
            {cfg.default_cc.map((e) => (
              <span key={e} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-blue-500/10 text-blue-300 border border-blue-500/30 rounded">
                {e}
                <button onClick={() => removeCc(e)} data-testid={`la-cfg-cc-rm-${e}`}
                  className="ml-1 text-blue-300/60 hover:text-red-400">
                  <X className="w-3 h-3"/>
                </button>
              </span>
            ))}
            <div className="inline-flex items-center gap-1">
              <input type="email" placeholder="office@firma.at" value={newCc}
                onChange={(e) => setNewCc(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCc())}
                data-testid="la-cfg-cc-input"
                className="px-2 py-1 bg-zinc-900 border border-zinc-800 rounded text-xs text-zinc-100 w-48"/>
              <button onClick={addCc} data-testid="la-cfg-cc-add"
                className="px-2 py-1 text-xs bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded">+</button>
            </div>
          </div>
          <div className="text-[10px] text-zinc-500 mt-2">
            Jede Lenkerauskunft erhält diese Empfänger automatisch als CC.
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-[10px] text-zinc-500">
            {cfg.updated_at ? `Zuletzt geändert: ${fmtDateTime(cfg.updated_at)} · ${cfg.updated_by || "?"}` : "Noch nicht konfiguriert"}
          </div>
          <button onClick={save} disabled={saving || !dirty}
            data-testid="la-cfg-save"
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 text-white rounded font-medium">
            {saving ? "Speichert…" : (dirty ? "Speichern *" : "Gespeichert")}
          </button>
        </div>
      </div>
    </div>
  );
}


function BuchhaltungConfig() {
  const [cfg, setCfg] = useState({ sender_email: "", sender_name: "", default_to: "", default_cc: [], updated_at: "", updated_by: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [newCc, setNewCc] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/buchhaltung");
      setCfg({
        sender_email: data.sender_email || "",
        sender_name: data.sender_name || "",
        default_to: data.default_to || "",
        default_cc: data.default_cc || [],
        updated_at: data.updated_at || "",
        updated_by: data.updated_by || "",
      });
      setDirty(false);
    } catch { toast.error("Konfiguration konnte nicht geladen werden"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function update(field, value) {
    setCfg(c => ({ ...c, [field]: value }));
    setDirty(true);
  }

  function addCc() {
    const v = (newCc || "").trim();
    if (!v || !v.includes("@")) { toast.error("Ungültige E-Mail"); return; }
    if (cfg.default_cc.map(x => x.toLowerCase()).includes(v.toLowerCase())) {
      toast.info("Bereits in Liste"); return;
    }
    update("default_cc", [...cfg.default_cc, v]);
    setNewCc("");
  }
  function removeCc(email) {
    update("default_cc", cfg.default_cc.filter(e => e !== email));
  }

  async function save() {
    if (cfg.sender_email && !cfg.sender_email.includes("@")) { toast.error("Ungültige Absender-E-Mail"); return; }
    if (cfg.default_to && !cfg.default_to.includes("@")) { toast.error("Ungültige Standard-Empfänger-E-Mail"); return; }
    setSaving(true);
    try {
      await api.put("/settings/buchhaltung", {
        sender_email: cfg.sender_email,
        sender_name: cfg.sender_name,
        default_to: cfg.default_to,
        default_cc: cfg.default_cc,
      });
      toast.success("Einstellungen gespeichert");
      setDirty(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  }

  if (loading) return <div className="p-6 text-zinc-400 text-sm">Lade…</div>;

  return (
    <div className="space-y-4" data-testid="buchhaltung-config">
      <div className="bg-zinc-900 border border-zinc-800 rounded p-6">
        <h3 className="font-display text-zinc-50 font-semibold mb-1">Buchhaltungs-E-Mail Versand</h3>
        <p className="text-xs text-zinc-500 mb-5">
          Konfiguriert Absender und Standard-Empfänger für E-Mails an die externe Buchhaltung (Anmeldung, Abmeldung, Mitteilungen).
        </p>

        <div className="grid grid-cols-2 gap-4 mb-5">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-zinc-500 block mb-1">Absender-E-Mail</label>
            <input value={cfg.sender_email || ""}
              onChange={(e) => update("sender_email", e.target.value)}
              placeholder="hr@deine-firma.at"
              data-testid="bh-cfg-sender-email"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
            <div className="text-[10px] text-zinc-500 mt-1">Muss bei Ihrem E-Mail-Provider (Resend) verifiziert sein.</div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-zinc-500 block mb-1">Absender-Name</label>
            <input value={cfg.sender_name || ""}
              onChange={(e) => update("sender_name", e.target.value)}
              placeholder="Firma – Personalabteilung"
              data-testid="bh-cfg-sender-name"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
          </div>
        </div>

        <div className="mb-5">
          <label className="text-[10px] uppercase tracking-wide text-zinc-500 block mb-1">Standard-Empfänger (An)</label>
          <input value={cfg.default_to || ""}
            onChange={(e) => update("default_to", e.target.value)}
            placeholder="buchhaltung@steuerberater.at"
            data-testid="bh-cfg-default-to"
            className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"/>
          <div className="text-[10px] text-zinc-500 mt-1">
            Diese E-Mail wird beim Anlegen neuer Buchhaltungs-Mails automatisch als primärer Empfänger eingetragen — pro Eintrag überschreibbar.
          </div>
        </div>

        <div className="bg-zinc-950 border border-zinc-800 rounded p-4 mb-5">
          <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2">Standard-Kopie-Empfänger</div>
          <div className="flex flex-wrap gap-1.5 items-center">
            {cfg.default_cc.map((e) => (
              <span key={e} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-blue-500/10 text-blue-300 border border-blue-500/30 rounded">
                {e}
                <button onClick={() => removeCc(e)} data-testid={`bh-cfg-cc-rm-${e}`}
                  className="ml-1 text-blue-300/60 hover:text-red-400">
                  <X className="w-3 h-3"/>
                </button>
              </span>
            ))}
            <div className="inline-flex items-center gap-1">
              <input type="email" placeholder="kopie@firma.at" value={newCc}
                onChange={(e) => setNewCc(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCc())}
                data-testid="bh-cfg-cc-input"
                className="px-2 py-1 bg-zinc-900 border border-zinc-800 rounded text-xs text-zinc-100 w-48"/>
              <button onClick={addCc} data-testid="bh-cfg-cc-add"
                className="px-2 py-1 text-xs bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded">+</button>
            </div>
          </div>
          <div className="text-[10px] text-zinc-500 mt-2">
            Jede Buchhaltungs-Mail erhält diese Empfänger automatisch als CC.
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-[10px] text-zinc-500">
            {cfg.updated_at ? `Zuletzt geändert: ${fmtDateTime(cfg.updated_at)} · ${cfg.updated_by || "?"}` : "Noch nicht konfiguriert"}
          </div>
          <button onClick={save} disabled={saving || !dirty}
            data-testid="bh-cfg-save"
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 text-white rounded font-medium">
            {saving ? "Speichert…" : (dirty ? "Speichern *" : "Gespeichert")}
          </button>
        </div>
      </div>
    </div>
  );
}



function PortalAutoDisablePanel() {
  const [days, setDays] = useState(30);
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    api.get("/settings/portal-auto-disable")
      .then(({ data }) => { setDays(data.days); setEnabled(data.enabled); })
      .catch(() => {});
  }, []);

  async function save() {
    setSaving(true);
    try {
      await api.put("/settings/portal-auto-disable", { days });
      setEnabled(days > 0);
      toast.success(days > 0 ? `Auto-Disable aktiv: ${days} Tage` : "Auto-Disable deaktiviert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally { setSaving(false); }
  }

  async function runNow() {
    if (!window.confirm(`Auto-Disable jetzt ausführen? Konten ohne Login seit ${days} Tagen werden deaktiviert.`)) return;
    setRunning(true);
    try {
      const { data } = await api.post("/employees/portal-auto-disable/run-now");
      setLastResult(data);
      toast.success(data.disabled === 0 ? "Keine Konten zu deaktivieren" : `${data.disabled} Konto(en) deaktiviert`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setRunning(false); }
  }

  return (
    <div>
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs text-zinc-400 uppercase tracking-wider">Tage Inaktivität</label>
        <input
          type="number" min={0} max={365} value={days}
          onChange={(e) => setDays(parseInt(e.target.value) || 0)}
          data-testid="portal-auto-disable-days"
          className="w-24 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"
        />
        <span className={`text-xs px-2 py-1 rounded ${enabled ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-800 text-zinc-500"}`}>
          {enabled ? `Aktiv (${days} Tage)` : "Deaktiviert"}
        </span>
        <span className="text-[10px] text-zinc-500 ml-2">0 = aus · läuft täglich um 07:15 Europe/Vienna</span>
        <button onClick={save} disabled={saving} data-testid="portal-auto-disable-save"
          className="ml-auto px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 text-white rounded font-medium">
          {saving ? "Speichere…" : "Speichern"}
        </button>
        <button onClick={runNow} disabled={running || !enabled} data-testid="portal-auto-disable-run"
          className="px-3 py-2 text-sm bg-amber-600 hover:bg-amber-700 disabled:bg-zinc-800 text-white rounded font-medium">
          {running ? "Läuft…" : "Jetzt ausführen"}
        </button>
      </div>
      {lastResult && (
        <div className="mt-3 text-xs text-zinc-400">
          Letzte Ausführung: <span className="text-zinc-200 font-medium">{lastResult.disabled}</span> Konto(en) deaktiviert (Cutoff {lastResult.days} Tage).
          {lastResult.first_5 && lastResult.first_5.length > 0 && (
            <div className="mt-1 text-[10px] italic">{lastResult.first_5.join(", ")}{lastResult.disabled > 5 ? "…" : ""}</div>
          )}
        </div>
      )}
    </div>
  );
}

